import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useT } from '../../i18n/LocaleContext';
import { formatDate } from '../../utils/format';
import { CATEGORY_TONE } from './constants';

const PRINT_CLASS = 'is-printing-doc';

function DocumentBody({ doc, t }) {
  const meta = [
    doc.meetingDate && { label: t('documents_page.field_meeting_date'), value: formatDate(doc.meetingDate, t) },
    doc.location && { label: t('documents_page.field_location'), value: doc.location },
    doc.authorName && { label: t('documents_page.field_author'), value: doc.authorName },
  ].filter(Boolean);

  return (
    <article className="doc-article">
      <header className="doc-article__header">
        <div className="doc-article__kicker">
          {t('app.name')} · {t(`documents_page.categories.${doc.category}`)}
          {doc.code && <span className="mono"> · {doc.code}</span>}
        </div>
        <h1 className="doc-article__title">{doc.title}</h1>
        {meta.length > 0 && (
          <dl className="doc-article__meta">
            {meta.map((m) => (
              <div key={m.label}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {doc.attendees && (
          <div className="doc-article__attendees">
            <span className="detail-item-label">{t('documents_page.field_attendees')}</span>
            <p>{doc.attendees}</p>
          </div>
        )}
      </header>
      {/* contentHtml is sanitised server-side (jsoup safelist). */}
      <div className="doc-content" dangerouslySetInnerHTML={{ __html: doc.contentHtml }} />
    </article>
  );
}

export default function DocumentViewModal({ isOpen, onClose, doc, onEdit, onDelete }) {
  const t = useT();

  useEffect(() => {
    const done = () => document.body.classList.remove(PRINT_CLASS);
    window.addEventListener('afterprint', done);
    return () => {
      window.removeEventListener('afterprint', done);
      done();
    };
  }, []);

  if (!doc) return null;

  // Print only the document: a hidden print-only copy lives directly under
  // <body>, and the body class hides everything else in @media print.
  const handlePrint = () => {
    document.body.classList.add(PRINT_CLASS);
    window.print();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={(
          <span className="doc-modal-title">
            <Badge tone={CATEGORY_TONE[doc.category]}>{t(`documents_page.categories.${doc.category}`)}</Badge>
          </span>
        )}
        subtitle={doc.updatedAt ? t('documents_page.updated_on', { date: formatDate(doc.updatedAt, t) }) : null}
        size="xl"
        footer={(
          <>
            {onDelete && <Button variant="ghost" className="doc-delete-btn" onClick={onDelete}>{t('common.delete')}</Button>}
            <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
            <Button variant="primary" onClick={handlePrint} iconLeft="🖨️">{t('documents_page.print')}</Button>
            {onEdit && <Button variant="gold" onClick={onEdit}>{t('common.edit')}</Button>}
          </>
        )}
      >
        <DocumentBody doc={doc} t={t} />
      </Modal>
      {isOpen && createPortal(
        <div className="doc-print-root" aria-hidden>
          <DocumentBody doc={doc} t={t} />
        </div>,
        document.body
      )}
    </>
  );
}
